import { useCallback, useEffect, useMemo, useState } from 'react';
import http from '../services/http';

function stringValue(value) {
  if (value === undefined || value === null) return '';
  const text = String(value).trim();
  return text === 'undefined' || text === 'null' ? '' : text;
}

function unwrapVisit(response) {
  const payload = response?.data ?? response ?? {};
  return payload?.visit || payload?.data?.visit || payload?.data || payload || {};
}

function normalizeVisit(raw) {
  const visit = raw && typeof raw === 'object' ? raw : {};
  const visitId = stringValue(visit.visitId || visit.id || visit._id);
  const citizenId = stringValue(visit.citizenId || visit.cid);

  return {
    ...visit,
    visitId,
    citizenId,
    serviceArea: stringValue(visit.serviceArea || visit.visitCategory),
    visitCategory: stringValue(visit.visitCategory || visit.serviceArea),
    clinicalPathway: stringValue(visit.clinicalPathway || visit.clinicalFocus),
    clinicalFocus: stringValue(visit.clinicalFocus || visit.clinicalPathway),
    complaintGroup: stringValue(visit.complaintGroup || visit.primaryPresentingProblem),
    primaryPresentingProblem: stringValue(
      visit.primaryPresentingProblem || visit.complaintGroup
    ),
    sectionProgress:
      visit.sectionProgress && typeof visit.sectionProgress === 'object'
        ? visit.sectionProgress
        : {},
    additionalPathways: Array.isArray(visit.additionalPathways)
      ? visit.additionalPathways
      : [],
  };
}

function plainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function firstNonEmptyArray(values = []) {
  return values.find((value) => Array.isArray(value) && value.length > 0) || [];
}

function returnedReviewDataFromResponse(response) {
  const payload = plainObject(response?.data ?? response);
  const data = plainObject(payload.data);
  const roots = [
    payload,
    data,
    plainObject(payload.visit),
    plainObject(data.visit),
    plainObject(payload.prescription),
    plainObject(data.prescription),
  ];

  const correctionItems = firstNonEmptyArray(
    roots.flatMap((root) => [
      root.correctionItems,
      root.returnItems,
      root.flaggedCorrections,
      root.reviewCorrections,
    ])
  );

  const returnSections = firstNonEmptyArray(
    roots.map((root) => root.returnSections)
  );

  const returnNote = roots
    .map((root) => stringValue(root.returnNote || root.note))
    .find(Boolean) || '';

  const returnedAt = roots
    .map((root) => root.returnedAt)
    .find(Boolean) || '';

  return {
    correctionItems,
    returnSections,
    returnNote,
    returnedAt,
  };
}

function hasReturnedReviewData(value = {}) {
  return Boolean(
    (Array.isArray(value.correctionItems) && value.correctionItems.length) ||
    (Array.isArray(value.returnSections) && value.returnSections.length) ||
    value.returnNote ||
    value.returnedAt
  );
}

async function fetchReturnedReviewData(visitId) {
  const encodedVisitId = encodeURIComponent(visitId);
  const paths = [
    `/api/visits/${encodedVisitId}/prescription`,
    `/api/visit-prescription/${encodedVisitId}`,
  ];

  let fallback = null;
  for (const path of paths) {
    try {
      const response = await http.get(path, { params: { cb: Date.now() } });
      const reviewData = returnedReviewDataFromResponse(response);
      if (hasReturnedReviewData(reviewData)) return reviewData;
      fallback = reviewData;
    } catch {
      // Returned-review hydration is supplemental. The core Visit response must
      // remain usable even when a compatibility endpoint is unavailable.
    }
  }

  return fallback;
}

function hydrateReturnedReviewData(visit, reviewData) {
  if (!reviewData || !hasReturnedReviewData(reviewData)) return visit;

  const prescription = plainObject(visit?.prescription);
  const correctionItems = Array.isArray(reviewData.correctionItems)
    ? reviewData.correctionItems
    : [];
  const returnSections = Array.isArray(reviewData.returnSections)
    ? reviewData.returnSections
    : [];

  return {
    ...visit,
    ...(correctionItems.length ? { correctionItems } : {}),
    ...(returnSections.length ? { returnSections } : {}),
    ...(reviewData.returnNote ? { returnNote: reviewData.returnNote } : {}),
    prescription: {
      ...prescription,
      ...(correctionItems.length ? { correctionItems } : {}),
      ...(returnSections.length ? { returnSections } : {}),
      ...(reviewData.returnNote ? { returnNote: reviewData.returnNote } : {}),
      ...(reviewData.returnedAt && !prescription.returnedAt
        ? { returnedAt: reviewData.returnedAt }
        : {}),
    },
  };
}

function persistWorkspaceContext(visit) {
  if (!visit?.visitId || !visit?.citizenId) return;

  const context = {
    visitId: visit.visitId,
    citizenId: visit.citizenId,
    centre: visit.centre || visit.citizenCentre || '',
    visitType: visit.visitType || visit.encounterType || '',
    visitCategory: visit.visitCategory || visit.serviceArea || '',
    serviceArea: visit.serviceArea || visit.visitCategory || '',
    clinicalPathway: visit.clinicalPathway || visit.clinicalFocus || '',
    clinicalFocus: visit.clinicalFocus || visit.clinicalPathway || '',
    complaintGroup: visit.complaintGroup || visit.primaryPresentingProblem || '',
    primaryPresentingProblem:
      visit.primaryPresentingProblem || visit.complaintGroup || '',
    maternalContext: visit.maternalContext || '',
    triagePriority: visit.triagePriority || '',
    ageGroupAtVisit: visit.ageGroupAtVisit || '',
    ageYearsAtVisit: visit.ageYearsAtVisit,
    citizenGender: visit.citizenGender || '',
    workflowMode: visit.workflowMode || '',
    workflowVersion: visit.workflowVersion,
    additionalPathways: visit.additionalPathways || [],
  };

  try {
    localStorage.setItem('activeVisitId', visit.visitId);
    localStorage.setItem('visitId', visit.visitId);
    localStorage.setItem(`activeVisitId:${visit.citizenId}`, visit.visitId);
    localStorage.setItem('activeCitizenId', visit.citizenId);
    localStorage.setItem('lastVisitedCitizenId', visit.citizenId);
    localStorage.setItem('activeVisitContext', JSON.stringify(context));
    localStorage.setItem(`activeVisitContext:${visit.visitId}`, JSON.stringify(context));
  } catch {
    // Workspace remains usable even if browser storage is unavailable.
  }
}

export default function useVisitWorkspace({ visitId, citizenId, persistActive = true }) {
  const expectedVisitId = stringValue(visitId);
  const expectedCitizenId = stringValue(citizenId);
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(Boolean(expectedVisitId));
  const [error, setError] = useState('');
  const [identityMismatch, setIdentityMismatch] = useState(false);

  const refresh = useCallback(async () => {
    if (!expectedVisitId) {
      setVisit(null);
      setError('Visit ID is required.');
      setLoading(false);
      return null;
    }

    try {
      setLoading(true);
      setError('');
      setIdentityMismatch(false);

      const response = await http.get(
        `/api/visits/${encodeURIComponent(expectedVisitId)}`
      );
      let normalized = normalizeVisit(unwrapVisit(response));

      const status = stringValue(
        normalized?.prescription?.status || normalized?.status
      ).toLowerCase();
      if (status === 'returned') {
        const reviewData = await fetchReturnedReviewData(expectedVisitId);
        normalized = hydrateReturnedReviewData(normalized, reviewData);
      }

      if (!normalized.visitId) {
        throw new Error('The Visit response did not contain a Visit ID.');
      }
      if (normalized.visitId !== expectedVisitId) {
        setIdentityMismatch(true);
        throw new Error('The loaded Visit does not match the requested Visit ID.');
      }
      if (
        expectedCitizenId &&
        normalized.citizenId &&
        normalized.citizenId !== expectedCitizenId
      ) {
        setIdentityMismatch(true);
        throw new Error('The loaded Visit belongs to a different citizen.');
      }

      if (persistActive) persistWorkspaceContext(normalized);
      setVisit(normalized);
      return normalized;
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          'Unable to load Visit workspace.'
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [expectedVisitId, expectedCitizenId, persistActive]);

  useEffect(() => {
    let active = true;
    refresh().then((loaded) => {
      if (!active || !loaded) return;
    });
    return () => {
      active = false;
    };
  }, [refresh]);

  const primaryContext = useMemo(() => {
    if (!visit) return {};
    return {
      visitId: visit.visitId,
      citizenId: visit.citizenId,
      ageGroupAtVisit: visit.ageGroupAtVisit || '',
      ageYearsAtVisit: visit.ageYearsAtVisit,
      sex: visit.citizenGender || '',
      centre: visit.centre || visit.citizenCentre || '',
      visitType: visit.visitType || visit.encounterType || '',
      serviceArea: visit.serviceArea || visit.visitCategory || '',
      clinicalPathway: visit.clinicalPathway || visit.clinicalFocus || '',
      presentingProblem:
        visit.primaryPresentingProblem || visit.complaintGroup || '',
      maternalContext: visit.maternalContext || '',
      triagePriority: visit.triagePriority || '',
    };
  }, [visit]);

  return {
    visit,
    primaryContext,
    sectionProgress: visit?.sectionProgress || {},
    additionalPathways: visit?.additionalPathways || [],
    loading,
    error,
    identityMismatch,
    refresh,
  };
}
